import { Boss } from './boss-base';
import { getTexture, makeSprite } from '@/assets/resolver';
import { effectsProfile } from '@/config/fx';
import type { BulletSystem } from '@/entities/bullets';
import { scaleMotion, type BulletMotion } from '@/entities/bullet-motion';
import type { TelegraphSink } from '@/render/telegraphs';
import {
  BROODWORM, COIL, COIL_TINT, EGG_INTERVAL, EGG_MOTION, EGG_RADIUS, EGG_SPEED, FAN_OFFSETS,
  FAN_WARN, FIRE_INTERVAL, HEAD_DMG_MUL, HEAD_R, HIT_FLASH, PATROL_R, PATROL_SPEED, SEG_COUNT,
  SEG_DIST, SEG_R, SHOT, SPEED, STEER, WARN_TINT, followNodes, leadNode, nearestJoint, nodeInBlast,
  nodesTouch, type Node,
} from './broodworm-parts';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';

export { BROODWORM } from './broodworm-parts';

type WormState = 'patrol' | 'tell' | 'coil';

/** Broodworm Queen — per-segment HP, coil + eggs (broodworm-parts.ts). */
export class BroodwormBoss extends Boss {
  private nodes: Node[] = [];
  private phase = 0;
  private vx = 0;
  private vy = 0;
  private state: WormState = 'patrol';
  private stateT = 0;
  private coilTimer = COIL.interval * COIL.firstAt;
  private coilX = 0;
  private coilY = 0;
  private coilA = 0;
  private coilR0: number = COIL.startR;
  private coilDir = 1;
  private fireTimer = FIRE_INTERVAL;
  private eggTimer = EGG_INTERVAL;
  private eggIndex = 0;
  private time = 0;
  private readonly egg: BulletMotion;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(BROODWORM, level, x, y, homeX, homeY);
    this.allowReinforce = false;
    this.body.visible = false;
    const total = SEG_COUNT + 1;
    const headHp = (this.maxHp / total) * 1.4; // tougher head
    const segHp = (this.maxHp - headHp) / SEG_COUNT;
    for (let i = 0; i <= SEG_COUNT; i++) {
      const head = i === 0;
      const sprite = makeSprite(head ? 'boss/broodworm/head' : 'boss/broodworm/segment');
      this.view.addChild(sprite);
      this.nodes.push({
        alive: true, hp: head ? headHp : segHp,
        x: homeX - i * SEG_DIST, y: homeY, prevX: homeX, prevY: homeY, head, sprite,
      });
    }
    this.egg = scaleMotion(EGG_MOTION, this.bulletSpeedMul)!;
    this.runners = this.makeRunners([], true); // tier evolve layers (A3)
  }

  protected override scaleParts(mul: number): void {
    for (const n of this.nodes) n.hp *= mul;
    this.syncHp();
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;
    this.time += dt;
    this.hitFlash -= dt;
    const head = leadNode(this.nodes);
    if (!head) { this.dead = true; return; }
    head.prevX = head.x;
    head.prevY = head.y;
    this.steer(head, dt, px, py);
    this.x = head.x;
    this.y = head.y;
    followNodes(this.nodes);
    this.fireFan(head, px, py, dt, bullets);
    this.layEggs(dt, bullets);
    this.fireRunners(dt, px, py, bullets, telegraphs);
  }

  /** Patrol → tell (head glows) → coil a shrinking ring around the locked spot. */
  private steer(head: Node, dt: number, px: number, py: number): void {
    const f = this.levelSpeedMul;
    let tx = head.x;
    let ty = head.y;
    let maxSpeed = SPEED * f;
    let steer = STEER;
    if (this.state === 'patrol') {
      this.phase += PATROL_SPEED * f * dt;
      tx = this.homeX + Math.cos(this.phase) * PATROL_R;
      ty = this.homeY + Math.sin(this.phase) * PATROL_R * 0.7;
      this.coilTimer -= dt;
      if (this.coilTimer <= 0) {
        this.coilTimer = COIL.interval / f;
        this.state = 'tell';
        this.stateT = COIL.tell;
      }
    } else if (this.state === 'tell') {
      tx = head.x + this.vx;
      ty = head.y + this.vy;
      maxSpeed *= COIL.tellSlow;
      this.stateT -= dt;
      if (this.stateT <= 0) {
        // Lock the coil on the ship's spot — the ring forms around it
        this.coilX = px;
        this.coilY = py;
        this.coilA = Math.atan2(head.y - py, head.x - px);
        const d = Math.hypot(head.x - px, head.y - py);
        this.coilR0 = Math.max(COIL.endR, Math.min(COIL.startR, d));
        this.coilDir = -this.coilDir;
        this.state = 'coil';
        this.stateT = 0;
      }
    } else {
      this.stateT += dt;
      const p = Math.min(1, this.stateT / COIL.time);
      const r = this.coilR0 + (COIL.endR - this.coilR0) * p;
      maxSpeed = COIL.speed * f;
      steer = COIL.steer;
      this.coilA += this.coilDir * (maxSpeed / r) * dt;
      tx = this.coilX + Math.cos(this.coilA) * r;
      ty = this.coilY + Math.sin(this.coilA) * r;
      if (p >= 1) this.state = 'patrol';
    }
    const dx = tx - head.x;
    const dy = ty - head.y;
    const dd = Math.hypot(dx, dy) || 1;
    this.vx += (dx / dd) * maxSpeed * steer * dt;
    this.vy += (dy / dd) * maxSpeed * steer * dt;
    const v = Math.hypot(this.vx, this.vy);
    if (v > maxSpeed) { this.vx = (this.vx / v) * maxSpeed; this.vy = (this.vy / v) * maxSpeed; }
    head.x += this.vx * dt;
    head.y += this.vy * dt;
  }

  /** Head aimed fan — tier tempo + bullet-speed ramp like every attack. */
  private fireFan(head: Node, px: number, py: number, dt: number, bullets: BulletSystem): void {
    this.fireTimer -= dt * this.intensity;
    if (this.fireTimer > 0) return;
    this.fireTimer = FIRE_INTERVAL;
    const base = Math.atan2(py - head.y, px - head.x);
    for (const off of FAN_OFFSETS) {
      bullets.spawn({
        x: head.x, y: head.y, dirX: Math.cos(base + off), dirY: Math.sin(base + off),
        speed: SHOT.fan * this.bulletSpeedMul, life: 4, radius: 10, damage: 1,
        friendly: false, texKey: 'bullet/enemy/spore', rotates: false,
      });
    }
  }

  /** A joint lays an egg sideways; it rolls to a stop and hatches (B3 split). */
  private layEggs(dt: number, bullets: BulletSystem): void {
    this.eggTimer -= dt * this.intensity;
    if (this.eggTimer > 0) return;
    this.eggTimer = EGG_INTERVAL;
    for (let k = 1; k <= SEG_COUNT; k++) {
      this.eggIndex = (this.eggIndex % SEG_COUNT) + 1;
      const seg = this.nodes[this.eggIndex]!;
      if (!seg.alive || seg.head) continue;
      const side = this.eggIndex % 2 === 0 ? 1 : -1;
      const a = Math.atan2(this.vy, this.vx) + (side * Math.PI) / 2;
      bullets.spawn({
        x: seg.x, y: seg.y, dirX: Math.cos(a), dirY: Math.sin(a),
        speed: EGG_SPEED * this.bulletSpeedMul, life: 5, radius: EGG_RADIUS, damage: 1,
        friendly: false, texKey: 'bullet/enemy/egg', rotates: false, motion: this.egg,
      });
      return;
    }
  }

  // ---- Per-segment health (user request): each node drops on its own ----

  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    let hits = 0;
    let dealt = 0;
    for (const n of this.nodes) {
      if (!n.alive) continue;
      const r = n.head ? HEAD_R : SEG_R;
      hits += bullets.collideCircle(true, n.x, n.y, r, (dmg) => {
        this.hitFlash = HIT_FLASH;
        if (this.damageLocked || !n.alive) return 0; // reinforcement squad first (X9)
        const taken = this.hurt(n, dmg);
        dealt += taken;
        return taken; // armor-scaled, as this node really took it
      });
    }
    this.syncHp();
    return { hits, dealt };
  }

  override takeDamage(amount: number): boolean {
    let target: Node | null = null;
    for (const n of this.nodes) if (n.alive && (!target || n.hp < target.hp)) target = n;
    if (!target || this.dead || this.damageLocked) return false;
    this.hitFlash = HIT_FLASH;
    this.hurt(target, amount);
    this.syncHp();
    return this.dead;
  }

  /** @returns damage dealt; the head's armor applies */
  private hurt(n: Node, amount: number): number {
    const eff = amount * (n.head ? HEAD_DMG_MUL : 1);
    const dealt = Math.min(n.hp, eff);
    n.hp -= eff;
    if (n.hp <= 0) this.killNode(n);
    return dealt;
  }

  override hitByAoe(x: number, y: number, radius: number): boolean {
    return nodeInBlast(this.nodes, x, y, radius) !== null;
  }

  /** Blasts hurt the node they land on — the head's armor applies (X5). */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (this.dead || this.damageLocked) return 0;
    const n = nodeInBlast(this.nodes, x, y, radius);
    if (!n) return 0;
    this.hitFlash = HIT_FLASH;
    this.markAoe(n.x, n.y, n.head ? HEAD_R : SEG_R);
    const dealt = this.hurt(n, damage);
    this.syncHp();
    return dealt;
  }

  /** Soft joints first: the nearest living segment, else the head (X7). */
  override aimPoint(px: number, py: number): { x: number; y: number } {
    return nearestJoint(this.nodes, px, py) ?? this;
  }

  override touchesPlayer(px: number, py: number, playerRadius: number): boolean {
    return nodesTouch(this.nodes, px, py, playerRadius);
  }

  private killNode(n: Node): void {
    if (!n.alive) return;
    n.alive = false;
    n.sprite.visible = false;
    this.events?.burst(n.x, n.y, BOSS_FEEDBACK.broodwormSegment);
    // Promote the next living node to head if the head fell
    if (n.head) {
      const next = leadNode(this.nodes);
      if (next) { next.head = true; next.sprite.texture = getTexture('boss/broodworm/head', 0); }
    }
  }

  private syncHp(): void {
    let hp = 0;
    let alive = 0;
    for (const n of this.nodes) {
      if (!n.alive) continue;
      alive++;
      hp += Math.max(0, n.hp);
    }
    this.hp = Math.max(0, hp); // unrounded: the bar drops by exactly the numbers shown
    if (alive === 0) this.dead = true;
  }

  override render(alpha: number): void {
    this.view.position.set(0, 0);
    const tell = this.state === 'tell' && Math.sin(this.time * 40) > 0;
    for (const n of this.nodes) {
      if (!n.alive) continue;
      n.sprite.position.set(n.prevX + (n.x - n.prevX) * alpha, n.prevY + (n.y - n.prevY) * alpha);
      if (n.head) n.sprite.rotation = Math.atan2(this.vy, this.vx) + Math.PI / 2;
      const warn = n.head && this.fireTimer < FAN_WARN * this.intensity;
      n.sprite.tint = tell ? COIL_TINT : warn ? WARN_TINT : this.levelTint;
      n.sprite.scale.set(this.hitFlash > 0 ? effectsProfile().bossPunch : 1);
    }
  }
}
